import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getJob, getJobVersion } from "../services/apiService";
import { alignLineItemResults } from "../utils/jobPayload";
import { getLineDisplayName } from "./workbench/estimate/qboLine";
import { formatJobDate } from "./workbench/JobsRail";
import "./QuoteView.css";
import "./PurchasingSheet.css";

/**
 * `/jobs/:jobId/order-sheet` — what to buy for the job's rafter tails.
 *
 * Same money rule as the quote page: stored numbers only, never a fresh
 * calculation. These are whole sheets and whole gallons for purchasing; they
 * are deliberately NOT the estimate's material allowance, and no dollar figure
 * appears here. Nothing is ordered from this page — stock IDs still need a
 * human check before a purchase order goes out.
 */

const fraction = (inches) =>
  ({ 0.5: "½", 0.125: "⅛" })[String(Number(inches))] || `${inches}`;

const size = (pair) =>
  Array.isArray(pair) ? pair.map((n) => Number(n)).join(" × ") : "—";

/** One row per stock run: sides and faces for every rafter-tail trial line. */
export function buildOrderSheet(version) {
  const lineItems = Array.isArray(version?.job_data?.line_items)
    ? version.job_data.line_items
    : [];
  const results = alignLineItemResults(version?.calculation_results, lineItems);
  const priced = Array.isArray(results?.line_item_results)
    ? results.line_item_results
    : [];

  const rows = [];
  const finish = [];
  // Trial lines that did not price are named, never silently dropped: a
  // sheet that looks complete would under-order the job.
  const missing = [];
  priced.forEach((item, index) => {
    const lineItem = lineItems[index];
    if (
      item?.error &&
      lineItem?.mode !== "custom" &&
      lineItem?.pricing_model === "rafter_tail_linked_v1"
    ) {
      missing.push({
        key: `${index}-missing`,
        line: getLineDisplayName(item, lineItem) || `Line ${index + 1}`,
        error: String(item.error),
      });
    }
    const working = item && !item.error ? item.rafter_tail : null;
    if (!working?.purchasing) return;
    const name =
      getLineDisplayName(item, lineItems[index]) || `Line ${index + 1}`;
    const { sides, faces } = working.purchasing;
    [
      ["Sides (CNC)", sides],
      ["Face skins (table saw)", faces],
    ].forEach(([part, detail]) => {
      if (!detail) return;
      rows.push({
        key: `${index}-${part}`,
        line: name,
        part,
        material: detail.material_class,
        thickness: `${fraction(detail.thickness_in)} in`,
        sheet: `${size(detail.sheet_in)} in`,
        blank: `${size(detail.blank_in)} in`,
        piecesRequired: Number(detail.pieces_required),
        perSheet: Number(detail.pieces_per_sheet),
        sheets: Number(detail.sheets_required),
      });
    });
    if (item.finish_mode_applied === "island_finish") {
      finish.push({
        key: `${index}-finish`,
        line: name,
        gallons: Number(working.finish_gallons),
        toOrder: Number(working.finish_gallons_to_order),
      });
    }
  });

  // What to actually buy: identical stock across lines adds up.
  const totals = new Map();
  rows.forEach((row) => {
    const stock = `${row.material} ${row.thickness} · ${row.sheet}`;
    totals.set(stock, (totals.get(stock) || 0) + row.sheets);
  });

  // Paint is one shared buy: add what the lines use, then round up once.
  // Rounding each line first would order a gallon per line.
  const paintUsed = finish.reduce((sum, row) => sum + row.gallons, 0);
  return {
    rows,
    finish,
    missing,
    stockTotals: Array.from(totals, ([stock, sheets]) => ({ stock, sheets })),
    paintToOrder: finish.length ? Math.ceil(paintUsed - 1e-9) : 0,
  };
}

const CSV_HEADER = [
  "Line",
  "Part",
  "Material",
  "Thickness",
  "Sheet",
  "Blank",
  "Pieces required",
  "Per sheet",
  "Sheets to buy",
];

const csvCell = (value) => {
  // A typed line name starting with = + - @ would run as a formula in Excel.
  const raw = String(value ?? "");
  const text = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function buildOrderSheetCsv(sheet) {
  const lines = [CSV_HEADER.join(",")];
  sheet.rows.forEach((row) =>
    lines.push(
      [
        row.line,
        row.part,
        row.material,
        row.thickness,
        row.sheet,
        row.blank,
        row.piecesRequired,
        row.perSheet,
        row.sheets,
      ]
        .map(csvCell)
        .join(","),
    ),
  );
  sheet.finish.forEach((row) =>
    lines.push(
      [
        row.line,
        "Paint / stain",
        "",
        "",
        "",
        "",
        `${row.gallons} gal used`,
        "",
        `${row.toOrder} gal`,
      ]
        .map(csvCell)
        .join(","),
    ),
  );
  (sheet.missing || []).forEach((row) =>
    lines.push(
      [row.line, "NOT ON THIS SHEET", "", "", "", "", row.error, "", ""]
        .map(csvCell)
        .join(","),
    ),
  );
  return `${lines.join("\n")}\n`;
}

function downloadCsv(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function PurchasingSheet() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [version, setVersion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const jobData = await getJob(jobId);
        const versionData = await getJobVersion(
          jobId,
          jobData.current_version_id,
        );
        if (!isMounted) return;
        setJob(jobData);
        setVersion(versionData);
      } catch (loadError) {
        console.error("Failed to load order sheet:", loadError);
        if (isMounted) setError("Couldn't load this job's order sheet.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [jobId, reloadToken]);

  const retry = useCallback(() => setReloadToken((token) => token + 1), []);

  if (isLoading) {
    return (
      <div className="quote-view">
        <p className="quote-view__state">Loading order sheet…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quote-view">
        <p className="quote-view__state quote-view__state--error" role="alert">
          {error}
        </p>
        <div className="quote-view__bar">
          <button type="button" className="wb-tbtn" onClick={retry}>
            Try again
          </button>
          <button
            type="button"
            className="wb-tbtn"
            onClick={() => navigate("/jobs")}
          >
            Back to saved jobs
          </button>
        </div>
      </div>
    );
  }

  const sheet = buildOrderSheet(version);
  const empty = sheet.rows.length === 0 && sheet.missing.length === 0;
  const fileName = `${(job?.job_name || "job").replace(/[^\w-]+/g, "-")}-order-sheet.csv`;

  return (
    <div className="quote-view">
      <div className="quote-view__bar quote-view__noprint">
        <button
          type="button"
          className="wb-tbtn"
          onClick={() => navigate("/jobs")}
        >
          Back to saved jobs
        </button>
        <button
          type="button"
          className="wb-tbtn"
          disabled={empty}
          onClick={() => downloadCsv(fileName, buildOrderSheetCsv(sheet))}
        >
          Download spreadsheet
        </button>
        <button
          type="button"
          className="wb-tbtn wb-tbtn--solid"
          disabled={empty}
          onClick={() => window.print()}
        >
          Print
        </button>
      </div>

      <article className="quote-doc order-doc">
        <header className="quote-doc__hd">
          <p className="order-doc__kicker">Order sheet · rafter tails</p>
          <h1 className="quote-doc__title">
            {job?.job_name || "Untitled job"}
          </h1>
          <p className="quote-doc__customer">
            {job?.customer_name || "No customer on file"}
          </p>
          <dl className="quote-doc__meta">
            <div>
              <dt>Date</dt>
              <dd>{formatJobDate(version?.created_at || job?.updated_at)}</dd>
            </div>
            <div>
              <dt>Version</dt>
              <dd>{version?.version_number ?? "—"}</dd>
            </div>
          </dl>
        </header>

        {empty ? (
          <p className="quote-doc__empty">
            No rafter-tail trial lines priced on this version.
          </p>
        ) : (
          <>
            {sheet.missing.length ? (
              <div className="quote-doc__incomplete" role="alert">
                <p>
                  Incomplete order sheet:{" "}
                  {sheet.missing.length === 1
                    ? "1 rafter-tail line did not price and is"
                    : `${sheet.missing.length} rafter-tail lines did not price and are`}{" "}
                  not included below.
                </p>
                <ul>
                  {sheet.missing.map((row) => (
                    <li key={row.key}>
                      <b>{row.line}</b> — {row.error}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {sheet.rows.length === 0 ? (
              <p className="quote-doc__empty">
                Nothing else to order on this version.
              </p>
            ) : (
              <>
                <h2 className="order-doc__h2">Buy</h2>
                <ul className="order-doc__buy">
                  {sheet.stockTotals.map((stock) => (
                    <li key={stock.stock}>
                      <b className="wb-num">{stock.sheets}</b> {stock.stock}{" "}
                      sheet
                      {stock.sheets === 1 ? "" : "s"}
                    </li>
                  ))}
                  {sheet.finish.length ? (
                    <li>
                      <b className="wb-num">{sheet.paintToOrder}</b> gal paint /
                      stain
                    </li>
                  ) : null}
                </ul>

                <h2 className="order-doc__h2">Layout</h2>
                <table className="quote-doc__table order-doc__table">
                  <thead>
                    <tr>
                      <th scope="col">Line · part</th>
                      <th scope="col">Stock</th>
                      <th scope="col">Blank</th>
                      <th scope="col" className="quote-doc__num">
                        Pieces
                      </th>
                      <th scope="col" className="quote-doc__num">
                        Per sheet
                      </th>
                      <th scope="col" className="quote-doc__num">
                        Sheets
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rows.map((row) => (
                      <tr key={row.key}>
                        <th scope="row">
                          <span className="quote-doc__name">{row.line}</span>
                          <span className="quote-doc__desc">{row.part}</span>
                        </th>
                        <td>
                          {row.material} {row.thickness}
                          <span className="quote-doc__desc">{row.sheet}</span>
                        </td>
                        <td>{row.blank}</td>
                        <td className="quote-doc__num">{row.piecesRequired}</td>
                        <td className="quote-doc__num">{row.perSheet}</td>
                        <td className="quote-doc__num">{row.sheets}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {sheet.finish.length ? (
                  <>
                    <h2 className="order-doc__h2">Paint / stain</h2>
                    <table className="quote-doc__table order-doc__table">
                      <thead>
                        <tr>
                          <th scope="col">Line</th>
                          <th scope="col" className="quote-doc__num">
                            Used
                          </th>
                          <th scope="col" className="quote-doc__num">
                            Order
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sheet.finish.map((row) => (
                          <tr key={row.key}>
                            <th scope="row">{row.line}</th>
                            <td className="quote-doc__num">
                              {row.gallons} gal
                            </td>
                            <td className="quote-doc__num">
                              {row.toOrder} gal
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : null}

                <p className="order-doc__note">
                  Simple grid layouts — no nesting. Side blanks include spacing
                  and sit inside a ¾ in sheet margin; face skins allow a ⅛ in
                  saw kerf. Paint is ordered in whole gallons; check what is
                  already on the shelf. Confirm stock IDs before a purchase
                  order goes out.
                </p>
              </>
            )}
          </>
        )}
      </article>
    </div>
  );
}

export default PurchasingSheet;
